import { comboKey } from "@/domain/profile-resolver/index";
import type { Binding, CharBind, Profile } from "@/domain/types";
import { SC2_BASE_PRESETS, STANDARD_DEFAULTS_TEXT } from "./defaults";
import { altParts, parseRaw, readSuffix, type SC2RawTable } from "./parser/index";
import { bindingFor, SELECTION, SMART_COMMAND } from "./parser/profile";
import {
  SC2_COMMANDS,
  SC2_ROSTERS,
  type SC2Cluster,
  type SC2Command,
  type SC2Roster,
  servingUnit,
} from "./sc2-registry";

/**
 * The registry verb ids each Blizzard cluster contributes (decision 0033). The smart verbs are
 * ordinary MP_COMMANDS entries (move/stop/hold/patrol/attack/cancel) keyed by the file's single
 * global Move=/Attack=/... entries, so the binding is file-faithful; only which verbs a card shows
 * is per-unit. Rendered as the per-Context underlay (Profile.contextBase), below the card.
 */
export const CLUSTER_VERBS: Record<SC2Cluster, string[]> = {
  basic: ["move", "stop", "hold", "patrol", "attack"],
  flying: ["move", "stop", "hold", "patrol"],
  tower: ["stop", "attack"],
};

/**
 * Curated [Hotkeys] ids whose Command belongs to one race. The section is race-agnostic in the file
 * and its entries land in the shared flatten tier, but Warp In is a Protoss warp-gate action: on a
 * Zerg or Terran card it is a binding the player does not have. The flatten scopes these to that
 * race's Contexts (CharBind.contexts) and leaves everything else universal - control groups,
 * cameras, Idle Worker and the rest are every race's. (What each id DISPLAYS as is the parser's
 * curation, parser/profile bindingFor; which Contexts it lands in is this ingest's, like every
 * other tier here.)
 */
const RACE_SCOPED_HOTKEYS: Record<string, SC2Roster["race"]> = { WarpIn: "protoss" };

/** Every Context id of each race, keyed exactly as `contexts` is (`roster/context`). A roster's
 * race is its faction, so a Protoss co-op commander's cards scope with Protoss. Built once. */
const CONTEXTS_BY_RACE: Record<string, ReadonlySet<string>> = (() => {
  const out: Record<string, Set<string>> = {};
  for (const [rosterId, roster] of Object.entries(SC2_ROSTERS)) {
    out[roster.race] ??= new Set<string>();
    const ids = out[roster.race];
    for (const ctx of roster.contexts) ids?.add(`${rosterId}/${ctx.id}`);
  }
  return out;
})();

/** Options for {@link ingestSC2}. */
export interface IngestSC2Options {
  /**
   * Fill the gaps from the Blizzard Standard table. A `.SC2Hotkeys` file is a delta against
   * an in-engine base preset, so a sparse upload (a player who rebound a few keys) is mostly absent;
   * with this set, every Command the file does not bind is filled from Standard, tagged
   * `source: "default"`. Production always fills, built-ins included (use-side) - the tag lets the
   * render dim or hide the fill. Leave unset only to ingest a file bare (tests, tooling).
   */
  fillDefaults?: boolean;
}

/** The Standard table, parsed once: the base a sparse upload is delta-merged onto, and the
 * source of the game-constant mouse defaults (identical across presets). */
const STANDARD_RAW: SC2RawTable = parseRaw(STANDARD_DEFAULTS_TEXT);

/** Each base preset parsed once; the file's `Suffix=` selects which (incl. the Grid bases
 * _GLS/_GRS). Unknown or absent Suffix -> Standard. */
const BASE_RAW: Record<string, SC2RawTable> = Object.fromEntries(
  Object.entries(SC2_BASE_PRESETS).map(([suffix, text]) => [suffix, parseRaw(text)]),
);
function baseRawForSuffix(suffix: string): SC2RawTable {
  return BASE_RAW[suffix] ?? STANDARD_RAW;
}

/**
 * Ingest a .SC2Hotkeys file into a character-space Profile: the per-Game step that turns
 * SC2's text source into the one shape the generic projector renders. Lens-independent - it captures
 * the file's TOKENS (not codes), so a single ingest re-projects through any Keyboard Layout. The
 * precedence tiers, lowest to highest: the per-Context smart-verb cluster underlay (decision 0033,
 * Profile.contextBase), the game-constant mouse defaults, the curated [Hotkeys] flatten (shared, the
 * flatten first-wins per AllowSetConflicts), and the per-Context command cards (highest). Contexts are
 * keyed `race/contextId`. The flatten is shared except for the race-only globals, which carry the
 * Contexts they exist in (RACE_SCOPED_HOTKEYS) and keep this same tier there.
 *
 * When `fillDefaults` is set (production always sets it) the Standard table is merged UNDER the file - the
 * file wins per [Hotkeys] id and per [Commands] cmd/unit, and every Command the file does not bind is
 * filled from Standard and tagged `source: "default"` so the render can dim or hide it.
 *
 * Which bucket serves an entry is an exact per-form lookup (servingUnit): the entry's own unit
 * bucket, else the flat `_def` - never another caster's bucket - so serving is lens-independent
 * by construction (a token's projectability no longer influences which row serves).
 */
export function ingestSC2(text: string, pin?: Profile["pin"], options?: IngestSC2Options): Profile {
  const file = parseRaw(text);
  // effective raw table: the file's declared base preset (Suffix=) merged under the file when filling,
  // the file alone otherwise. An unknown Suffix falls back to Standard; _GLS/_GRS select the Grid bases.
  const base = baseRawForSuffix(readSuffix(text));
  const raw = options?.fillDefaults ? mergeRaw(base, file) : file;
  // a Command/hotkey is "from the file" when the file's raw table holds it; otherwise it was filled
  // from Standard (source: "default"). Built-ins (no fill) leave `file` === `raw`, so all are "file".

  // the curated [Hotkeys] flatten, in file order so first-wins matches hotkeysProfile
  const flatten: CharBind[] = [];
  for (const [id, value] of Object.entries(raw.hotkeys)) {
    if (MOUSE_IDS.has(id) && !(id in file.hotkeys)) continue; // game-constant mouse: the MOUSE tier owns it (below)
    const binding = bindingFor(id);
    if (!binding) continue; // a rule curated this id out (menu / social / UI), or diverted TargetChoose
    const source = id in file.hotkeys ? undefined : "default";
    // a race-only global lands on that race's Contexts alone; everything else is universal
    const race = RACE_SCOPED_HOTKEYS[id];
    const contexts = race ? CONTEXTS_BY_RACE[race] : undefined;
    for (const alt of splitAlternates(value)) {
      flatten.push({
        binding: { ...binding, ...(source ? { source } : {}) },
        token: alt.token,
        combo: alt.combo,
        ...(contexts ? { contexts } : {}),
      });
    }
  }

  const contexts: Record<string, CharBind[]> = {};
  // the per-Context smart-verb underlay (decision 0033): each card's Blizzard cluster + Cancel,
  // replacing the old universal globals tier. Below the card so a real ability on a shared key wins.
  const contextBase: Record<string, CharBind[]> = {};
  for (const [raceId, race] of Object.entries(SC2_ROSTERS)) {
    for (const ctx of race.contexts) {
      // the Context's own token: the Grid table's per-Context `key/<roster>/<ctx>` rows key on it,
      // so this Context resolves the exact grid face for a command that grids differently elsewhere.
      const contextToken = `${raceId}/${ctx.id}`;
      const card: CharBind[] = [];
      for (const id of ctx.card) {
        const entry = SC2_COMMANDS[id];
        if (entry) pushCommand(card, id, entry, raw, file, contextToken);
      }
      contexts[`${raceId}/${ctx.id}`] = card;

      const verbs: CharBind[] = [];
      const verbIds = [
        ...(ctx.cluster ? CLUSTER_VERBS[ctx.cluster] : []),
        ...(ctx.cancel ? ["cancel"] : []),
      ];
      for (const id of verbIds) {
        const entry = SC2_COMMANDS[id];
        if (entry) pushCommand(verbs, id, entry, raw, file, contextToken);
      }
      if (verbs.length > 0) contextBase[`${raceId}/${ctx.id}`] = verbs;
    }
  }

  // the [Hotkeys] flatten is first-file-wins (AllowSetConflicts: the earlier entry is primary, e.g.
  // Edennil's AIClearAll/AIDelete/MinimapPing colliding on one combo+key). buildTier now wins
  // first-placement natively (decision 0034), so the flatten is emitted in file order - no reversal.
  const profile: Profile = {
    id: "sc2",
    name: "SC2",
    base: [mouseDefaults(), flatten],
    contextBase,
    contexts,
    // SC2's engine avoids same-card conflicts itself, so the projector yields a contested
    // multi-key alternate to its free key (decision 0042)
    alternateYield: true,
  };
  if (pin) profile.pin = pin;
  return profile;
}

// SC2's left/right click actions are NOT written to any .SC2Hotkeys file, so they are game constants
// shown on every SC2 Profile. They are real `UI/Hotkey` Standard defaults (Selection / SmartCommand),
// so the VALUES are read from the vendored Standard table - no hotkey is hard-authored in code. The
// middle button has no game default, so it stays unbound. Lowest-precedence tier, so a file
// that genuinely rebinds a mouse zone (via its own [Hotkeys] entry, higher tier) still wins.
const MOUSE_IDS = new Set(["Selection", "SmartCommand"]);
// Each id shares one spec with its [Hotkeys] make-rule (parser/profile), so a file that rebinds the
// action onto a key cannot clobber this curated mouse-zone binding: same name + note + icon ride the
// command onto whatever key (or button) holds it.
const MOUSE_META: Record<string, { name: string; category: string; cmd: string; note: string }> = {
  Selection: SELECTION,
  SmartCommand: SMART_COMMAND,
};

/** The game-constant mouse bindings, valued from the vendored Standard table. */
function mouseDefaults(): CharBind[] {
  const out: CharBind[] = [];
  for (const [id, meta] of Object.entries(MOUSE_META)) {
    const value = STANDARD_RAW.hotkeys[id];
    if (!value) continue;
    for (const alt of splitAlternates(value)) {
      out.push({
        binding: {
          name: meta.name,
          category: meta.category,
          cmd: meta.cmd,
          note: meta.note,
          source: "default",
        },
        token: alt.token,
        combo: alt.combo,
      });
    }
  }
  return out;
}

/**
 * Merge a base preset under a file: the file wins. Hotkeys merge per id, keeping the FILE's
 * insertion order for every id the file binds - the flatten is first-entry-wins per
 * AllowSetConflicts, and a plain spread re-keys a colliding id to the BASE's position, flipping the
 * primary (the shipped Edennil trio on Ctrl+Shift+Alt+X). Base-only ids follow, as fills.
 * Commands merge per unit: the file owns exactly the units it binds; the base keeps its _def and
 * sibling units as real config. Which bucket then serves an entry is servingUnit's exact
 * per-form lookup - a per-unit file row on a unit-less (universal) entry is dead text by design,
 * and only the base's RECONSTRUCTION rows (Grid faces) are fromFile-guarded against masking a
 * rebind the file really made.
 */
function mergeRaw(base: SC2RawTable, over: SC2RawTable): SC2RawTable {
  const hotkeys: SC2RawTable["hotkeys"] = { ...over.hotkeys };
  for (const [id, value] of Object.entries(base.hotkeys)) {
    if (!(id in hotkeys)) hotkeys[id] = value;
  }
  const commands: SC2RawTable["commands"] = { ...base.commands };
  for (const [key, byUnit] of Object.entries(over.commands)) {
    commands[key] = { ...commands[key], ...byUnit };
  }
  return { hotkeys, commands };
}

/** Emit a registry command's alternates as CharBinds onto a tier (mirrors sc2.ts placeCommand). */
function pushCommand(
  out: CharBind[],
  id: string,
  entry: SC2Command,
  raw: SC2RawTable,
  file: SC2RawTable,
  contextToken?: string,
): void {
  const served = servingValue(entry, raw, file, contextToken);
  if (served == null) return;
  const fromFile = file.commands[entry.key]?.[served.unit] !== undefined;
  const binding = { name: entry.name, category: entry.category, cmd: id } as Binding;
  if (entry.short) binding.short = entry.short;
  if (entry.note) binding.note = entry.note;
  if (!fromFile) binding.source = "default";
  for (const alt of splitAlternates(served.value)) {
    out.push({ binding: { ...binding }, token: alt.token, combo: alt.combo });
  }
}

/**
 * The raw file value that serves a registry command, in token space, plus which unit served it (for
 * provenance). Selects through the shared servingUnit lookup (see its doc), telling it which
 * buckets the FILE itself binds so a base-only Grid reconstruction row (per-Context or per-unit
 * face) cannot mask the file's own rebind, then returns that bucket's verbatim token string
 * instead of parsed codes. render-aux hotkeyFor selects through the same lookup on the bare
 * (unmerged) table, where every bucket is the file's.
 */
function servingValue(
  entry: SC2Command,
  raw: SC2RawTable,
  file: SC2RawTable,
  contextToken?: string,
): { value: string; unit: string } | undefined {
  const rawByUnit = raw.commands[entry.key];
  if (!rawByUnit) return undefined;
  const fileByUnit = file.commands[entry.key];
  const unit = servingUnit(
    rawByUnit,
    entry.unit,
    fileByUnit ? (u) => fileByUnit[u] !== undefined : undefined,
    contextToken,
  );
  if (unit === undefined) return undefined;
  const value = rawByUnit[unit];
  return value === undefined ? undefined : { value, unit };
}

/**
 * Split a raw file value into its alternates' token + combo (mirrors sc2-parser parseToken's split,
 * minus the token->code projection): comma-separated alternatives, each `Modifier+...+Key` with the
 * modifiers named Control / Shift / Alt. A bare-modifier key (e.g. CameraCenter=Alt, TheCore's Center
 * on Selection on a tapped Alt - an intentional collision with the Alt modifier) is KEPT as a
 * named-modifier token: the projector's MODIFIER_TOKEN resolves it onto the modifier caps, so a single
 * modifier lands on the base layer and a modifier chord on the layer its combo names. The binding then
 * renders rather than vanishing. Other unmappable tokens (mouse buttons, F13) are kept here too and
 * dropped at projection (tokenToCodes returns nothing), the same net effect as parseToken filtering them.
 */
function splitAlternates(value: string): Array<{ token: string; combo: string }> {
  return altParts(value).map(({ mods, key }) => ({ token: key, combo: comboKey(mods) }));
}

// [Commands] keys the registry models as universal buttons (every entry unit-less): one shared
// flat binding in game, so a per-unit row on them is dead text. Keys with any unit-bearing entry
// are excluded - an unknown qualifier there may be a caster keylens just doesn't card.
const UNIVERSAL_KEYS: ReadonlySet<string> = (() => {
  const unitless = new Set<string>();
  const qualified = new Set<string>();
  for (const entry of Object.values(SC2_COMMANDS)) {
    (entry.unit === undefined ? unitless : qualified).add(entry.key);
  }
  for (const key of qualified) unitless.delete(key);
  return unitless;
})();

/**
 * File [Commands] lines keylens will not render AND whose loss is visible: per-unit rows on a
 * key the registry models as universal (one shared flat binding, so the per-unit line is dead
 * text in game too) whose value differs from the flat row that WILL render - the file's own,
 * else the base preset's default. The import preview names them so a hand-written rebind does
 * not silently vanish. Skipped as invisible: value-identical twins (every TheCore file carries
 * `StopGenerateCreep=X` + `/Overlord=X`), values matching the base default the fill shows
 * anyway, and empty values (an unbind row binds nothing under any reading). Left alone with no
 * claim either way: Grid's per-Context reconstruction tokens (a "/" inside the bucket), keys
 * the registry does not curate, and unmatched qualifiers on keys with unit-bearing entries -
 * those may be real casters keylens simply does not card (TheCore files carry many), so
 * flagging them would false-alarm on every TheCore upload.
 */
export function inertCommandRows(text: string): string[] {
  const file = parseRaw(text);
  const base = baseRawForSuffix(readSuffix(text));
  const out: string[] = [];
  for (const [key, byUnit] of Object.entries(file.commands)) {
    if (!UNIVERSAL_KEYS.has(key)) continue;
    const rendered = byUnit._def ?? base.commands[key]?._def;
    for (const [bucket, value] of Object.entries(byUnit)) {
      if (bucket === "_def" || bucket.includes("/")) continue;
      if (value !== "" && rendered !== value) out.push(`${key}/${bucket}`);
    }
  }
  return out.sort();
}
